import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import LanguageToggle from '../LanguageToggle';
import LoginButton from '../LoginButton';

const HeaderContainer = styled.header`
  background: ${props => props.theme.colors.bgSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  position: sticky;
  top: 0;
  z-index: 100;
`;

const Nav = styled.nav`
  max-width: 960px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 12px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
`;

const Logo = styled(Link)`
  font-size: 0.85rem;
  font-weight: 700;
  font-family: ${props => props.theme.fonts.mono};
  text-decoration: none;
  color: ${props => props.theme.colors.primary};
  letter-spacing: 0.5px;
  flex-shrink: 0;
  padding-right: 8px;
  margin-right: 4px;
  border-right: 1px solid ${props => props.theme.colors.border};
  line-height: 1.15;

  &:hover { opacity: 0.8; }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.78rem;
    padding-right: 6px;
    margin-right: 2px;
  }
`;

const LogoSub = styled.span`
  display: block;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0;
  opacity: 0.7;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: ${props => props.$isActive ? props.theme.colors.text : props.theme.colors.textMuted};
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 6px;
  transition: all 0.2s;
  font-size: 0.78rem;
  white-space: nowrap;

  ${props => props.$isActive && `
    background: ${props.theme.colors.bgTertiary};
    color: ${props.theme.colors.text};
  `}

  &:hover {
    color: ${props => props.theme.colors.text};
    background: ${props => props.theme.colors.bgTertiary};
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    gap: 4px;
  }
`;

const DesktopOnly = styled.div`
  display: contents;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileLoginButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 12px 20px;
  margin: 4px 0;
  background: #fff;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  color: #333;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: ${props => props.theme.fonts.main};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f5f5f5;
  }
`;

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const HamburgerButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  flex-shrink: 0;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

const MobileMenuOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: ${props => props.$open ? 'block' : 'none'};
  }
`;

const MobileMenu = styled.div`
  display: none;
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.bgSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  z-index: 100;
  padding: 8px 0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: ${props => props.$open ? 'block' : 'none'};
  }
`;

const MobileNavLink = styled(Link)`
  display: block;
  text-decoration: none;
  color: ${props => props.$isActive ? props.theme.colors.text : props.theme.colors.textSecondary};
  font-weight: 500;
  padding: 12px 20px;
  font-size: 0.9rem;
  transition: all 0.2s;

  ${props => props.$isActive && `
    background: ${props.theme.colors.bgTertiary};
    color: ${props.theme.colors.text};
    border-left: 3px solid ${props.theme.colors.primary};
  `}

  &:hover {
    background: ${props => props.theme.colors.bgTertiary};
    color: ${props => props.theme.colors.text};
  }
`;

const HamburgerIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    {open ? (
      <>
        <line x1="4" y1="4" x2="14" y2="14" />
        <line x1="14" y1="4" x2="4" y2="14" />
      </>
    ) : (
      <>
        <line x1="3" y1="4.5" x2="15" y2="4.5" />
        <line x1="3" y1="9" x2="15" y2="9" />
        <line x1="3" y1="13.5" x2="15" y2="13.5" />
      </>
    )}
  </svg>
);

const MobileMenuDivider = styled.div`
  height: 1px;
  background: ${props => props.theme.colors.border};
  margin: 4px 0;
`;

const MobileLogoutButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.error || '#EF4444'};
  font-weight: 500;
  padding: 12px 20px;
  font-size: 0.9rem;
  font-family: ${props => props.theme.fonts.main};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.colors.bgTertiary};
  }
`;

const Header = () => {
  const location = useLocation();
  const { t } = useLanguage();
  const { isAuthenticated, login, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Detect if we're inside a company workspace (/company/:slug/...)
  const companySlugMatch = location.pathname.match(/^\/company\/([^/]+)/);
  const companySlug = companySlugMatch ? companySlugMatch[1] : null;
  const isCompanyRoute = companySlug && companySlug !== 'create' && companySlug !== 'join';

  // Check if user navigated here from B2B context (via ?company= query param)
  const searchParams = new URLSearchParams(location.search);
  const companyParam = searchParams.get('company');

  // B2B mode: on /company/:slug routes OR on shared pages with ?company= param
  const activeCompanySlug = isCompanyRoute ? companySlug : companyParam;
  const isCompanyWorkspace = !!activeCompanySlug;

  // Remember the last visited company slug for "back to teams" navigation
  useEffect(() => {
    if (activeCompanySlug) {
      localStorage.setItem('struct-code-last-company', activeCompanySlug);
    }
  }, [activeCompanySlug]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  // B2B nav items when inside a company workspace
  const navItems = isCompanyWorkspace ? [
    { to: `/company/${activeCompanySlug}`, label: t('ダッシュボード', 'Dashboard'), match: `/company/${activeCompanySlug}` },
    { to: `/company/${activeCompanySlug}/members`, label: t('メンバー', 'Members'), match: `/company/${activeCompanySlug}/members` },
    { to: `/company/${activeCompanySlug}/ai-report`, label: t('AIレポート', 'AI Report'), match: `/company/${activeCompanySlug}/ai-report` },
    { to: `/current-diagnosis?company=${activeCompanySlug}`, label: t('診断する', 'Diagnose'), match: '/current-diagnosis' },
    { to: `/company/${activeCompanySlug}/settings`, label: t('設定', 'Settings'), match: `/company/${activeCompanySlug}/settings` },
    { to: '/', label: t('個人向け', 'Personal') },
  ] : [
    // B2C only — B2B (`/company`) は公開導線から除外 (2026-05-15)
    { to: '/', label: t('ホーム', 'Home'), match: '/' },
    { to: '/current-diagnosis', label: t('診断', 'Diagnose'), match: '/current-diagnosis', altMatch: '/natal-diagnosis' },
    { to: '/types', label: t('24タイプ', '24 Types'), match: '/types' },
    { to: '/chat', label: t('AIチャット', 'AI Chat'), match: '/chat' },
    ...(isAuthenticated ? [{ to: '/account', label: t('アカウント', 'Account'), match: '/account' }] : []),
  ];

  return (
    <HeaderContainer>
      <Nav>
        <LeftSection>
          <Logo to={isCompanyWorkspace ? `/company/${activeCompanySlug}` : '/'}>
            STRUCT CODE
            {isCompanyWorkspace && <LogoSub>for Teams</LogoSub>}
          </Logo>

          {/* Desktop nav */}
          <NavLinks>
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                $isActive={item.match ? (isActive(item.match) || (item.altMatch && isActive(item.altMatch))) : false}
              >
                {item.label}
              </NavLink>
            ))}
          </NavLinks>
        </LeftSection>

        <RightSection>
          <LanguageToggle />
          <DesktopOnly><LoginButton /></DesktopOnly>
          <HamburgerButton onClick={() => setMobileMenuOpen(prev => !prev)}>
            <HamburgerIcon open={mobileMenuOpen} />
          </HamburgerButton>
        </RightSection>
      </Nav>

      {/* Mobile menu */}
      <MobileMenuOverlay $open={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />
      <MobileMenu $open={mobileMenuOpen} ref={menuRef}>
        {navItems.map(item => (
          <MobileNavLink
            key={item.to}
            to={item.to}
            $isActive={item.match ? (isActive(item.match) || (item.altMatch && isActive(item.altMatch))) : false}
            onClick={() => setMobileMenuOpen(false)}
          >
            {item.label}
          </MobileNavLink>
        ))}
        {isAuthenticated ? (
          <>
            <MobileMenuDivider />
            <MobileNavLink to="/my-diagnoses" onClick={() => setMobileMenuOpen(false)}>
              {t('マイ診断', 'My Diagnoses')}
            </MobileNavLink>
            {/* B2B 「マイ組織 / 組織に参加」は公開導線から除外 (2026-05-15) */}
            <MobileMenuDivider />
            <MobileLogoutButton onClick={() => { logout(); setMobileMenuOpen(false); }}>
              {t('ログアウト', 'Logout')}
            </MobileLogoutButton>
          </>
        ) : (
          <>
            <MobileMenuDivider />
            <div style={{ padding: '8px 16px' }}>
              <MobileLoginButton onClick={() => { login(); setMobileMenuOpen(false); }}>
                <GoogleIcon />
                {t('Googleでログイン', 'Sign in with Google')}
              </MobileLoginButton>
            </div>
          </>
        )}
      </MobileMenu>
    </HeaderContainer>
  );
};

export default Header;
